import {
  Box, Typography, IconButton, Tooltip, Divider,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Paper, Chip, TextField,
} from '@mui/material';
import {
  NavigateNext as NextPhaseIcon,
  NavigateBefore as PrevPhaseIcon,
  Flag as PhaseIcon,
  StickyNote2 as NoteIcon,
  CheckCircle as FinalizeIcon,
  Replay as ReopenIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTerminalStatus } from './positionStatus';

const AtsPhaseBar = ({
  phases,
  currentPhase,
  position,
  canEdit = true,
  updatingPhase,
  closingProcess,
  onAdvancePhase,
  onRetreatPhase,
  onFinalizeProcess,
  phaseHistory,
}) => {
  const { t } = useTranslation();

  const [phaseNotesOpen, setPhaseNotesOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeNote, setFinalizeNote] = useState('');

  if (!phases || phases.length === 0) return null;

  // Cerrado y Cancelado son terminales: ambos congelan el pipeline
  const isClosed = isTerminalStatus(position?.status);

  const handleCloseFinalizeDialog = () => {
    setFinalizeDialogOpen(false);
    setFinalizeNote('');
  };

  return (
    <>
      <Box sx={{
        px: 3, py: 1.5,
        backgroundColor: '#F5F3FF',
        borderBottom: '1px solid #DDD6FE',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
        <PhaseIcon sx={{ fontSize: 18, color: '#6A5AF9' }} />
        <Typography variant="body2" color="text.secondary">
          {t('ats.detail.currentPhase')}:
        </Typography>
        <Typography variant="body2" fontWeight={700} color="#4C1D95">
          {currentPhase?.name || '—'}
        </Typography>

        {phaseHistory && phaseHistory.filter((h) => h.note).length > 0 && (
          <Tooltip title={t('ats.detail.viewPhaseNotes')}>
            <IconButton size="small" onClick={() => setPhaseNotesOpen(true)} sx={{ ml: 0.5 }}>
              <NoteIcon sx={{ fontSize: 16, color: '#6A5AF9' }} />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          {phases.map((phase) => (
            <Tooltip key={phase.id} title={phase.name}>
              <Box sx={{
                width: 8, height: 8,
                borderRadius: '50%',
                backgroundColor: phase.order <= (currentPhase?.order || 0) ? '#6A5AF9' : '#DDD6FE',
              }} />
            </Tooltip>
          ))}

          {canEdit && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tooltip title={t('ats.detail.retreatPhase')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onRetreatPhase}
                    disabled={currentPhase?.order === 1 || updatingPhase || isClosed}
                  >
                    <PrevPhaseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t('ats.detail.advancePhase')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onAdvancePhase}
                    disabled={currentPhase?.order === phases.length || updatingPhase || isClosed}
                    sx={{ color: currentPhase?.order !== phases.length ? '#6A5AF9' : undefined }}
                  >
                    {updatingPhase ? <CircularProgress size={16} /> : <NextPhaseIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tooltip title={isClosed ? t('ats.detail.reopenProcess') : t('ats.detail.finalizeProcess')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setFinalizeDialogOpen(true)}
                    disabled={closingProcess}
                    sx={{ color: isClosed ? '#10B981' : '#EF4444' }}
                  >
                    {closingProcess
                      ? <CircularProgress size={16} />
                      : isClosed
                        ? <ReopenIcon fontSize="small" />
                        : <FinalizeIcon fontSize="small" />
                    }
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Dialog finalizar/reabrir */}
      <Dialog open={finalizeDialogOpen} onClose={handleCloseFinalizeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isClosed ? t('ats.detail.reopenProcessTitle') : t('ats.detail.finalizeProcessTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isClosed ? t('ats.detail.reopenProcessConfirm') : t('ats.detail.finalizeProcessConfirm')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('ats.detail.finalizeNote')}
            placeholder={isClosed
              ? t('ats.detail.finalizeNotePlaceholderReopen')
              : t('ats.detail.finalizeNotePlaceholderClose')
            }
            value={finalizeNote}
            onChange={(e) => setFinalizeNote(e.target.value)}
            helperText={t('ats.detail.finalizeNoteHelper')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFinalizeDialog} color="inherit" disabled={closingProcess}>
            {t('cancel')}
          </Button>
          <Button
            onClick={() => {
              onFinalizeProcess(finalizeNote || null);
              handleCloseFinalizeDialog();
            }}
            variant="contained"
            disabled={closingProcess}
            startIcon={closingProcess ? <CircularProgress size={16} color="inherit" /> : null}
            color={isClosed ? 'success' : 'error'}
          >
            {isClosed ? t('ats.detail.reopenProcess') : t('ats.detail.finalizeProcess')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog notas de fase */}
      <Dialog open={phaseNotesOpen} onClose={() => setPhaseNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('ats.detail.phaseNotesTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[...(phaseHistory || [])]
              .filter((h) => h.note)
              .sort((a, b) => (b.changedAt?._seconds || 0) - (a.changedAt?._seconds || 0))
              .map((entry, index) => (
                <Paper key={index} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {entry.fromPhaseId && (
                        <Chip
                          label={phases.find((p) => p.id === entry.fromPhaseId)?.name || entry.fromPhaseId}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {entry.toPhaseId && (
                        <>
                          <Typography variant="caption" color="text.secondary">→</Typography>
                          <Chip
                            label={phases.find((p) => p.id === entry.toPhaseId)?.name || entry.toPhaseId}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#6A5AF9', color: '#fff' }}
                          />
                        </>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                      {entry.changedAt?._seconds
                        ? new Date(entry.changedAt._seconds * 1000).toLocaleDateString('es-CL')
                        : '—'
                      }
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{entry.note}</Typography>
                  {entry.changedBy && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      {entry.changedBy}
                    </Typography>
                  )}
                </Paper>
              ))
            }
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhaseNotesOpen(false)} color="inherit">{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AtsPhaseBar;